/**
 * Employer Workflow Orchestration Dashboard
 *
 * Risk-categorized view for managing agentic workflows for employees:
 * - Dashboard stats by risk level
 * - Pending workflow queue with bulk/individual initiation
 * - Workflow execution tracking
 * - Referral status monitoring
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Users,
  Phone,
  Mail,
  MessageSquare,
  RefreshCw,
  Filter,
  ChevronDown,
  ChevronUp,
  Zap,
  Target,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Settings,
  MoreVertical,
  Send,
  Calendar,
  Building,
  User,
  Briefcase,
  Activity,
  Brain,
  FileText,
  ExternalLink,
  Loader2,
  Globe,
  Link2,
  Share2,
  Copy,
  Check,
  Plus,
  Edit2,
  Trash2,
  Save,
  Search,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { EmployeeDetailPanel } from './EmployeeDetailPanel';
import { ServiceReferralsList, ServiceReferralWorkspace } from '@/components/service-referrals';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

interface DashboardStats {
  critical_pending: number;
  high_pending: number;
  elevated_pending: number;
  moderate_pending: number;
  total_pending: number;
  total_queued: number;
  total_in_progress: number;
  total_waiting_response: number;
  completed_30d: number;
  successful_30d: number;
  declined_30d: number;
  no_response_30d: number;
  referrals_created_30d: number;
  referrals_completed_30d: number;
}

interface AIModelConfig {
  primary_model: string;
  decision_model: string;
  communication_model: string;
  personalization_model?: string;
  routing_model?: string;
  model_parameters: {
    temperature: number;
    max_tokens: number;
  };
}

interface PricingAgentConfig {
  enabled: boolean;
  channel: 'email' | 'sms' | 'voice' | 'portal';
  strategy: 'balanced' | 'aggressive' | 'accommodating' | 'urgent';
  max_rounds: number;
  auto_initiate: boolean;
  escalate_on_timeout: boolean;
  timeout_hours: number;
}

interface WorkflowTemplate {
  id: number;
  template_name: string;
  template_type: string;
  description: string;
  trigger_risk_levels: string[];
  trigger_min_churn_probability: number;
  trigger_min_sdoh_score: number;
  auto_trigger_enabled: boolean;
  default_channel: string;
  use_ml_timing: boolean;
  max_retry_attempts: number;
  response_timeout_hours: number;
  auto_create_referral: boolean;
  referral_service_types: string[];
  max_providers_to_match: number;
  cbo_only_for_sdoh: boolean;
  ai_model_config?: AIModelConfig;
  pricing_agent_config?: PricingAgentConfig;
  workflow_steps: any[];
  is_active: boolean;
}

interface PendingWorkflow {
  workflow_id: number;
  employee_id: number;
  employee_name: string;
  department: string | null;
  job_title: string | null;
  risk_level: string;
  churn_probability: number;
  engagement_score: number;
  sdoh_score: number;
  risk_factor_count: number;
  preferred_channel: string | null;
  best_contact_day: string | null;
  best_contact_time: string | null;
  action_plan: any[];
  contributing_factors: Record<string, any>;
  status: string;
  created_at: string;
}

interface AtRiskEmployee {
  employee_id: number;
  employee_name: string;
  department: string | null;
  job_title: string | null;
  email: string | null;
  phone_number: string | null;
  member_id: string | null;
  churn_probability: number;
  engagement_score: number;
  risk_level: string;
  contributing_factors: Record<string, any>;
  has_active_workflow: boolean;
  workflow_status: string | null;
  // Communication preferences
  preferred_channel: string | null;
  best_contact_day: string | null;
  best_contact_time: string | null;
  preferred_language: string;
  sms_enabled: boolean;
  email_enabled: boolean;
}

interface EmployerWorkflowOrchestrationProps {
  employerId: number;
  onNavigate?: (tab: string) => void;
}

// Risk level styling helpers
const getRiskBadgeVariant = (riskLevel: string): 'destructive' | 'default' | 'secondary' | 'outline' => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
      return 'destructive';
    case 'high':
      return 'default';
    case 'elevated':
      return 'secondary';
    default:
      return 'outline';
  }
};

const getRiskColor = (riskLevel: string): string => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
      return 'text-red-600';
    case 'high':
      return 'text-orange-600';
    case 'elevated':
      return 'text-amber-600';
    default:
      return 'text-green-600';
  }
};

const getRiskBgColor = (riskLevel: string): string => {
  // Return neutral background - no colored backgrounds for employee list
  return 'bg-background';
};

const getChannelIcon = (channel: string | null) => {
  switch (channel?.toLowerCase()) {
    case 'phone':
    case 'voice':
      return <Phone className="h-4 w-4" />;
    case 'sms':
      return <MessageSquare className="h-4 w-4" />;
    case 'email':
    default:
      return <Mail className="h-4 w-4" />;
  }
};

const getLanguageLabel = (code: string): string => {
  const languages: Record<string, string> = {
    'en': 'English',
    'es': 'Spanish',
    'zh': 'Chinese',
    'vi': 'Vietnamese',
    'ko': 'Korean',
    'tl': 'Tagalog',
    'ar': 'Arabic',
    'fr': 'French',
    'de': 'German',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'ja': 'Japanese',
    'hi': 'Hindi',
    'bn': 'Bengali',
  };
  return languages[code?.toLowerCase()] || code?.toUpperCase() || 'EN';
};

const generateShortLink = (employeeId: number): string => {
  return `wv.in/e/${employeeId.toString(36)}`;
};

export const EmployerWorkflowOrchestration: React.FC<EmployerWorkflowOrchestrationProps> = ({
  employerId,
  onNavigate,
}) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { token: contextToken } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [pendingWorkflows, setPendingWorkflows] = useState<PendingWorkflow[]>([]);
  const [atRiskEmployees, setAtRiskEmployees] = useState<AtRiskEmployee[]>([]);
  const [totalEmployeeCount, setTotalEmployeeCount] = useState<number>(0);
  const [selectedEmployees, setSelectedEmployees] = useState<Set<number>>(new Set());
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [initiating, setInitiating] = useState(false);
  const [activeTab, setActiveTab] = useState('queue');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [showInitiateDialog, setShowInitiateDialog] = useState(false);
  const [showConfigureDialog, setShowConfigureDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WorkflowTemplate | null>(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateFormData, setTemplateFormData] = useState<Partial<WorkflowTemplate>>({});
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  // Employee detail panel state
  const [selectedEmployeeForDetail, setSelectedEmployeeForDetail] = useState<{
    id: number;
    name: string;
  } | null>(null);
  // Service referrals state
  const [selectedReferralId, setSelectedReferralId] = useState<number | null>(null);

  const getAuthToken = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;
    // Check both auth_token (admin/CBO portal) and member_token (member/employer portal)
    return contextToken || localStorage.getItem('auth_token') || localStorage.getItem('member_token');
  }, [contextToken]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      console.warn('⚠️ WorkflowOrchestration: No auth token available');
      return;
    }

    if (!employerId || employerId === 0) {
      console.warn('⚠️ WorkflowOrchestration: Invalid employerId:', employerId);
      setError('No employer selected. Please select an employer from organization settings.');
      setLoading(false);
      return;
    }

    console.log('🔄 WorkflowOrchestration: Fetching data for employerId:', employerId);
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel
      const [statsRes, templatesRes, pendingRes, atRiskRes] = await Promise.all([
        authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/dashboard`),
        authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/templates`),
        authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/pending?limit=100`),
        authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/at-risk-employees?limit=100`),
      ]);

      console.log('📊 WorkflowOrchestration API responses:', {
        stats: statsRes.status,
        templates: templatesRes.status,
        pending: pendingRes.status,
        atRisk: atRiskRes.status,
      });

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData);
        if (templatesData.length > 0 && !selectedTemplate) {
          setSelectedTemplate(templatesData[0].id);
        }
      }

      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingWorkflows(pendingData);
      }

      if (atRiskRes.ok) {
        const atRiskData = await atRiskRes.json();
        console.log('👥 WorkflowOrchestration: At-risk employees received:', atRiskData?.length || 0, 'employees');
        if (atRiskData?.length > 0) {
          console.log('👥 First employee sample:', atRiskData[0]);
        }
        setAtRiskEmployees(atRiskData);
        setTotalEmployeeCount(atRiskData?.length || 0);
      } else {
        const errorText = await atRiskRes.text();
        console.error('❌ WorkflowOrchestration: at-risk-employees API error:', atRiskRes.status, errorText);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, selectedTemplate, employerId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle employee selection
  const handleSelectEmployee = (employeeId: number, checked: boolean) => {
    setSelectedEmployees(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(employeeId);
      } else {
        newSet.delete(employeeId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (employees: AtRiskEmployee[], checked: boolean) => {
    if (checked) {
      setSelectedEmployees(new Set(employees.map(e => e.employee_id)));
    } else {
      setSelectedEmployees(new Set());
    }
  };

  // Initiate workflows
  const handleInitiateWorkflows = async () => {
    if (selectedEmployees.size === 0 || !selectedTemplate) return;

    setInitiating(true);
    const authToken = getAuthToken();

    try {
      const response = await authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/initiate`, {
        method: 'POST',
        jsonBody: {
          employee_ids: Array.from(selectedEmployees),
          template_id: selectedTemplate,
          initiation_type: selectedEmployees.size > 1 ? 'bulk' : 'manual',
        }
      });

      if (response.ok) {
        const result = await response.json();
        setShowInitiateDialog(false);
        setSelectedEmployees(new Set());
        fetchDashboardData();
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to initiate workflows');
      }
    } catch (err) {
      console.error('Error initiating workflows:', err);
      setError('Failed to initiate workflows');
    } finally {
      setInitiating(false);
    }
  };

  // Initiate all at-risk
  const handleInitiateAllAtRisk = async () => {
    if (!selectedTemplate) return;

    setInitiating(true);
    const authToken = getAuthToken();

    try {
      const response = await authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/initiate-at-risk?template_id=${selectedTemplate}&min_churn_probability=0.5`, {
        method: 'POST'
      });

      if (response.ok) {
        fetchDashboardData();
      }
    } catch (err) {
      console.error('Error initiating at-risk workflows:', err);
    } finally {
      setInitiating(false);
    }
  };

  // Copy short link to clipboard
  const handleCopyLink = (employeeId: number) => {
    const link = `https://${generateShortLink(employeeId)}`;
    navigator.clipboard.writeText(link);
    setCopiedLinkId(employeeId);
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  // Open template dialog for creating new template
  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateFormData({
      template_name: '',
      template_type: 'employee_outreach',
      description: '',
      trigger_risk_levels: ['critical', 'high'],
      trigger_min_churn_probability: 0.4,
      trigger_min_sdoh_score: 50,
      auto_trigger_enabled: false,
      default_channel: 'ml_selected',
      use_ml_timing: true,
      max_retry_attempts: 3,
      response_timeout_hours: 48,
      auto_create_referral: true,
      referral_service_types: ['benefits_navigation', 'sdoh_support'],
      max_providers_to_match: 4,
      cbo_only_for_sdoh: true,
      pricing_agent_config: {
        enabled: true,
        channel: 'email',
        strategy: 'balanced',
        max_rounds: 5,
        auto_initiate: true,
        escalate_on_timeout: true,
        timeout_hours: 48,
      },
      is_active: true,
      workflow_steps: [
        { name: 'Initial Contact', type: 'outreach', order: 1 },
        { name: 'Follow Up', type: 'reminder', order: 2 },
        { name: 'Pricing Negotiation', type: 'pricing_agent', order: 3 },
        { name: 'Escalation', type: 'escalate', order: 4 },
      ],
    });
    setShowTemplateDialog(true);
  };

  // Open template dialog for editing existing template
  const handleEditTemplate = (template: WorkflowTemplate) => {
    setEditingTemplate(template);
    setTemplateFormData({ ...template });
    setShowTemplateDialog(true);
  };

  // Save template (create or update)
  const handleSaveTemplate = async () => {
    if (!templateFormData.template_name) {
      setError('Template name is required');
      return;
    }

    setSavingTemplate(true);
    const authToken = getAuthToken();

    try {
      const url = editingTemplate
        ? `${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/templates/${editingTemplate.id}`
        : `${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/templates`;

      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method: method,
        jsonBody: templateFormData
      });

      if (response.ok) {
        setShowTemplateDialog(false);
        setEditingTemplate(null);
        setTemplateFormData({});
        fetchDashboardData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save template');
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    const authToken = getAuthToken();

    try {
      const response = await authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/workflows/templates/${templateId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchDashboardData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete template');
      }
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    }
  };

  // Filter employees by risk level and search query
  const filteredAtRisk = atRiskEmployees.filter(e => {
    const matchesRisk = riskFilter === 'all' || e.risk_level === riskFilter;
    const matchesSearch = !searchQuery ||
      e.employee_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (e.department?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (e.job_title?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (e.email?.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesRisk && matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredAtRisk.length / pageSize);
  const paginatedEmployees = filteredAtRisk.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [riskFilter, searchQuery]);

  // Eligible employees (not already in workflow)
  const eligibleEmployees = filteredAtRisk.filter(e => !e.has_active_workflow);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
            <p className="text-muted-foreground">Loading orchestration dashboard...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            {!tabbed && (
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Brain className="h-6 w-6 text-purple-600" />
              Workflow Orchestration Center
            </h2>
            )}
            <p className="text-muted-foreground mt-1">
              Manage agentic workflows for at-risk employees
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchDashboardData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowConfigureDialog(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Configure
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Critical Risk */}
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Critical</p>
                  <p className="text-3xl font-bold text-red-600">{stats?.critical_pending || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">100% automation recommended</p>
            </CardContent>
          </Card>

          {/* High Risk */}
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">High Risk</p>
                  <p className="text-3xl font-bold text-orange-600">{stats?.high_pending || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">67% automation recommended</p>
            </CardContent>
          </Card>

          {/* In Progress */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">In Progress</p>
                  <p className="text-3xl font-bold text-blue-600">{stats?.total_in_progress || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Activity className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{stats?.total_waiting_response || 0} awaiting response</p>
            </CardContent>
          </Card>

          {/* Completed */}
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Completed (30d)</p>
                  <p className="text-3xl font-bold text-green-600">{stats?.completed_30d || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {stats?.successful_30d || 0} successful, {stats?.referrals_created_30d || 0} referrals
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="queue" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              At-Risk Queue
              {(stats?.critical_pending || 0) + (stats?.high_pending || 0) > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                  {(stats?.critical_pending || 0) + (stats?.high_pending || 0)}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Play className="h-4 w-4" />
              Active Workflows
              {(stats?.total_in_progress || 0) > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                  {stats?.total_in_progress || 0}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="referrals" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="admin" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Templates
              <Badge variant="outline" className="ml-1 h-5 px-1.5">
                {templates.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          {/* At-Risk Queue Tab */}
          <TabsContent value="queue" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>At-Risk Employee Queue</CardTitle>
                    <CardDescription>
                      Employees identified for workflow intervention based on ML predictions
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search employees..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-[200px]"
                      />
                      {searchQuery && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1 h-6 w-6 p-0"
                          onClick={() => setSearchQuery('')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>

                    {/* Risk Filter */}
                    <Select value={riskFilter} onValueChange={setRiskFilter}>
                      <SelectTrigger className="w-[150px]">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Risk Level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Levels</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="elevated">Elevated</SelectItem>
                        <SelectItem value="moderate">Moderate</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Template Selection */}
                    <Select
                      value={selectedTemplate?.toString() || ''}
                      onValueChange={(v) => setSelectedTemplate(parseInt(v))}
                      disabled={templates.length === 0}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder={templates.length === 0 ? "No templates" : "Select Template"} />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.length === 0 ? (
                          <SelectItem value="none" disabled>No templates available</SelectItem>
                        ) : (
                          templates.map(t => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.template_name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>

                    {/* Bulk Actions */}
                    {selectedEmployees.size > 0 && (
                      <Button
                        onClick={() => setShowInitiateDialog(true)}
                        disabled={!selectedTemplate || templates.length === 0}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Initiate ({selectedEmployees.size})
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      onClick={handleInitiateAllAtRisk}
                      disabled={initiating || !selectedTemplate || templates.length === 0}
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      Auto-Initiate Critical
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredAtRisk.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    {searchQuery ? (
                      <>
                        <p>No employees found matching "{searchQuery}"</p>
                        <p className="text-sm mt-1">Try adjusting your search or filters</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={() => {
                            setSearchQuery('');
                            setRiskFilter('all');
                          }}
                        >
                          Clear filters
                        </Button>
                      </>
                    ) : (
                      <>
                        <p>No at-risk employees found</p>
                        <p className="text-sm mt-1">All employees are within acceptable risk thresholds</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Select All Header with Count */}
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg mb-4">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedEmployees.size > 0 && selectedEmployees.size === eligibleEmployees.length}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedEmployees(new Set(eligibleEmployees.map(e => e.employee_id)));
                            } else {
                              setSelectedEmployees(new Set());
                            }
                          }}
                        />
                        <span className="text-sm font-medium">
                          {selectedEmployees.size > 0
                            ? `${selectedEmployees.size} of ${eligibleEmployees.length} selected`
                            : `Select all (${eligibleEmployees.length} eligible)`}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Showing {paginatedEmployees.length} of {filteredAtRisk.length} employees
                        {totalEmployeeCount > filteredAtRisk.length && ` (${totalEmployeeCount} total)`}
                      </div>
                    </div>

                    {/* Employee List - Paginated */}
                    {paginatedEmployees.map((employee) => (
                      <div
                        key={employee.employee_id}
                        className={`flex items-center gap-4 p-4 rounded-lg border ${
                          employee.has_active_workflow
                            ? 'bg-muted/50 opacity-60'
                            : getRiskBgColor(employee.risk_level)
                        }`}
                      >
                        <div className="flex items-center justify-center w-8">
                          <Checkbox
                            checked={selectedEmployees.has(employee.employee_id)}
                            onCheckedChange={(checked) =>
                              handleSelectEmployee(employee.employee_id, checked as boolean)
                            }
                            disabled={employee.has_active_workflow}
                            className="h-5 w-5 border-2 border-primary data-[state=checked]:bg-primary"
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <button
                              className="font-medium text-left hover:text-primary hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                              onClick={() => setSelectedEmployeeForDetail({
                                id: employee.employee_id,
                                name: employee.employee_name,
                              })}
                              title="Click to view details and actions"
                            >
                              {employee.employee_name}
                            </button>
                            <Badge variant={getRiskBadgeVariant(employee.risk_level)}>
                              {employee.risk_level.toUpperCase()}
                            </Badge>
                            {employee.has_active_workflow && (
                              <Badge variant="secondary">
                                {employee.workflow_status}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            {employee.department && (
                              <span className="flex items-center gap-1">
                                <Building className="h-3 w-3" />
                                {employee.department}
                              </span>
                            )}
                            {employee.job_title && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="h-3 w-3" />
                                {employee.job_title}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Communication Channels */}
                        <div className="flex flex-col items-center px-3 min-w-[80px]">
                          <div className="flex items-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className={`p-1.5 rounded ${employee.email_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                  <Mail className="h-3.5 w-3.5" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{employee.email_enabled ? 'Email enabled' : 'Email disabled'}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className={`p-1.5 rounded ${employee.sms_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                  <MessageSquare className="h-3.5 w-3.5" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{employee.sms_enabled ? 'SMS enabled' : 'SMS disabled'}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className={`p-1.5 rounded ${employee.preferred_channel === 'phone' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                                  <Phone className="h-3.5 w-3.5" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                {employee.preferred_channel === 'phone' ? 'Prefers phone' : 'Phone available'}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Channels</p>
                        </div>

                        {/* Language */}
                        <div className="flex flex-col items-center px-3 min-w-[70px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                {employee.preferred_language?.toUpperCase() || 'EN'}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>{getLanguageLabel(employee.preferred_language)}</TooltipContent>
                          </Tooltip>
                          <p className="text-xs text-muted-foreground mt-1">Language</p>
                        </div>

                        {/* Short Link */}
                        <div className="flex flex-col items-center px-3 min-w-[90px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyLink(employee.employee_id);
                                }}
                              >
                                {copiedLinkId === employee.employee_id ? (
                                  <Check className="h-3 w-3 mr-1 text-green-600" />
                                ) : (
                                  <Link2 className="h-3 w-3 mr-1" />
                                )}
                                {generateShortLink(employee.employee_id).split('/').pop()}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {copiedLinkId === employee.employee_id ? 'Copied!' : 'Click to copy engagement link'}
                            </TooltipContent>
                          </Tooltip>
                          <p className="text-xs text-muted-foreground">Share Link</p>
                        </div>

                        <div className="text-center px-3">
                          <p className={`text-xl font-bold ${getRiskColor(employee.risk_level)}`}>
                            {Math.round(employee.churn_probability * 100)}%
                          </p>
                          <p className="text-xs text-muted-foreground">Churn Risk</p>
                        </div>

                        <div className="text-center px-3">
                          <p className="text-xl font-bold">{Math.round(employee.engagement_score)}</p>
                          <p className="text-xs text-muted-foreground">Engagement</p>
                        </div>

                        <div className="flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant={employee.has_active_workflow ? "secondary" : "default"}
                                onClick={() => {
                                  if (!employee.has_active_workflow && selectedTemplate) {
                                    setSelectedEmployees(new Set([employee.employee_id]));
                                    setShowInitiateDialog(true);
                                  }
                                }}
                                disabled={!selectedTemplate || employee.has_active_workflow || templates.length === 0}
                              >
                                <Play className="h-4 w-4 mr-1" />
                                {employee.has_active_workflow ? 'Active' : 'Start'}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {employee.has_active_workflow
                                ? 'Workflow already active'
                                : !selectedTemplate || templates.length === 0
                                  ? 'Select a template first'
                                  : 'Start Workflow'}
                            </TooltipContent>
                          </Tooltip>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() => {
                                  // Open employee detail panel with consent/referral management
                                  setSelectedEmployeeForDetail({
                                    id: employee.employee_id,
                                    name: employee.employee_name,
                                  });
                                }}
                              >
                                <Activity className="h-4 w-4 mr-2" />
                                View Details & Actions
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => {
                                  // Navigate to employee profile
                                  if (employee.member_id) {
                                    window.open(`/members/${employee.member_id}`, '_blank');
                                  }
                                }}
                              >
                                <User className="h-4 w-4 mr-2" />
                                View Member Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => {
                                  // Show action plan in alert/modal - could be enhanced with a dialog
                                  const factors = employee.contributing_factors;
                                  const factorsList = factors ? Object.entries(factors)
                                    .map(([key, value]) => `${key}: ${value}`)
                                    .join('\n') : 'No action plan data available';
                                  alert(`Action Plan for ${employee.employee_name}:\n\nContributing Factors:\n${factorsList}`);
                                }}
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                View Action Plan
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => {
                                  // Initiate manual call - open phone dialer
                                  if (employee.phone_number) {
                                    window.open(`tel:${employee.phone_number}`, '_self');
                                  } else {
                                    alert('No phone number available for this employee');
                                  }
                                }}
                              >
                                <Phone className="h-4 w-4 mr-2" />
                                Manual Call
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => {
                                  // Open email client
                                  if (employee.email) {
                                    window.open(`mailto:${employee.email}?subject=Wellness Outreach`, '_self');
                                  } else {
                                    alert('No email address available for this employee');
                                  }
                                }}
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                Send Email
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t mt-4">
                        <div className="text-sm text-muted-foreground">
                          Page {currentPage} of {totalPages} ({filteredAtRisk.length} total employees)
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          >
                            First
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum: number;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              return (
                                <Button
                                  key={pageNum}
                                  variant={currentPage === pageNum ? "default" : "outline"}
                                  size="sm"
                                  className="w-8 h-8 p-0"
                                  onClick={() => setCurrentPage(pageNum)}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            Last
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Active Workflows Tab */}
          <TabsContent value="active" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Active Workflows</CardTitle>
                <CardDescription>
                  Track workflows currently in progress or awaiting employee response
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingWorkflows.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No active workflows</p>
                    <p className="text-sm mt-1">Initiate workflows from the At-Risk Queue</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingWorkflows.map((workflow) => (
                      <div
                        key={workflow.workflow_id}
                        className="flex items-center gap-4 p-4 rounded-lg border bg-background"
                      >
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          workflow.status === 'in_progress' ? 'bg-blue-100' :
                          workflow.status === 'waiting_response' ? 'bg-amber-100' :
                          workflow.status === 'queued' ? 'bg-gray-100' :
                          'bg-green-100'
                        }`}>
                          {workflow.status === 'in_progress' ? (
                            <Activity className="h-5 w-5 text-blue-600" />
                          ) : workflow.status === 'waiting_response' ? (
                            <Clock className="h-5 w-5 text-amber-600" />
                          ) : workflow.status === 'queued' ? (
                            <Pause className="h-5 w-5 text-gray-600" />
                          ) : (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{workflow.employee_name}</span>
                            <Badge variant={getRiskBadgeVariant(workflow.risk_level)}>
                              {workflow.risk_level}
                            </Badge>
                            <Badge variant="outline">
                              {workflow.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            {workflow.department && (
                              <span>{workflow.department}</span>
                            )}
                            {workflow.preferred_channel && (
                              <span className="flex items-center gap-1">
                                {getChannelIcon(workflow.preferred_channel)}
                                {workflow.preferred_channel}
                              </span>
                            )}
                            <span>Started {fmt.short(workflow.created_at)}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="mt-4">
            {selectedReferralId ? (
              // Show detailed workspace when a referral is selected
              <ServiceReferralWorkspace
                referralId={selectedReferralId}
                onClose={() => setSelectedReferralId(null)}
                userRole="employer"
              />
            ) : (
              // Show referrals list
              <ServiceReferralsList
                employerId={employerId}
                onSelectReferral={(referralId) => setSelectedReferralId(referralId)}
                onCreateNew={() => onNavigate?.('create-referral')}
              />
            )}
          </TabsContent>

          {/* Admin / Templates Tab */}
          <TabsContent value="admin" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Workflow Templates</CardTitle>
                    <CardDescription>
                      Configure and manage workflow templates for employee outreach
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleNewTemplate}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Template
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {templates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No workflow templates configured</p>
                    <p className="text-sm mt-1">Create templates to automate employee outreach</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className="p-4 rounded-lg border bg-background"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{template.template_name}</span>
                              <Badge variant={template.is_active ? 'default' : 'secondary'}>
                                {template.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge variant="outline">
                                {template.template_type.replace('_', ' ')}
                              </Badge>
                              {template.auto_trigger_enabled && (
                                <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                                  <Zap className="h-3 w-3 mr-1" />
                                  Auto
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {template.description || 'No description'}
                            </p>

                            {/* Template Configuration Details */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t">
                              <div>
                                <p className="text-xs text-muted-foreground">Risk Levels</p>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {template.trigger_risk_levels?.map((level: string) => (
                                    <Badge key={level} variant="outline" className="text-xs px-1.5">
                                      {level}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Default Channel</p>
                                <p className="text-sm font-medium flex items-center gap-1 mt-0.5">
                                  {getChannelIcon(template.default_channel)}
                                  {template.default_channel === 'ml_selected' ? 'ML Selected' : template.default_channel}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Response Timeout</p>
                                <p className="text-sm font-medium mt-0.5">{template.response_timeout_hours || 48}h</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Max Retries</p>
                                <p className="text-sm font-medium mt-0.5">{template.max_retry_attempts || 3}</p>
                              </div>
                            </div>

                            {/* AI Model Configuration */}
                            {template.ai_model_config && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                                  <Brain className="h-3 w-3" />
                                  AI Model Configuration
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div>
                                    <p className="text-xs text-muted-foreground">Primary Model</p>
                                    <p className="text-sm font-medium mt-0.5">{template.ai_model_config.primary_model}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Decision Model</p>
                                    <p className="text-sm font-medium mt-0.5">{template.ai_model_config.decision_model}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Comms Model</p>
                                    <p className="text-sm font-medium mt-0.5">{template.ai_model_config.communication_model}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Temperature</p>
                                    <p className="text-sm font-medium mt-0.5">{template.ai_model_config.model_parameters?.temperature || 0.3}</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Referral Settings */}
                            {template.auto_create_referral && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                                  <Target className="h-3 w-3" />
                                  Auto-Referral Settings
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {template.referral_service_types?.map((svc: string) => (
                                    <Badge key={svc} variant="outline" className="text-xs">
                                      {svc.replace('_', ' ')}
                                    </Badge>
                                  ))}
                                  <Badge variant={template.cbo_only_for_sdoh ? 'default' : 'outline'} className="text-xs">
                                    {template.cbo_only_for_sdoh ? 'CBO Only for SDOH' : 'All Providers'}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    Max {template.max_providers_to_match || 4} providers
                                  </Badge>
                                  {template.pricing_agent_config?.enabled !== false && (
                                    <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700">
                                      🤖 AI Pricing
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 ml-4">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditTemplate(template)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit Template</TooltipContent>
                            </Tooltip>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => handleEditTemplate(template)}>
                                  <Edit2 className="h-4 w-4 mr-2" />
                                  Edit Template
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() => {
                                    // Duplicate the template
                                    setEditingTemplate(null);
                                    setTemplateFormData({
                                      ...template,
                                      template_name: `${template.template_name} (Copy)`,
                                    });
                                    setShowTemplateDialog(true);
                                  }}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() => {
                                    // Navigate to workflow history or show history dialog
                                    if (onNavigate) {
                                      onNavigate('history');
                                    }
                                  }}
                                >
                                  <Activity className="h-4 w-4 mr-2" />
                                  View History
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onSelect={() => handleDeleteTemplate(template.id)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        {/* Workflow Steps Preview */}
                        {template.workflow_steps && template.workflow_steps.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-muted-foreground mb-2">Workflow Steps ({template.workflow_steps.length})</p>
                            <div className="flex items-center gap-2 overflow-x-auto pb-2">
                              {template.workflow_steps.map((step: any, index: number) => (
                                <div key={index} className="flex items-center">
                                  <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs whitespace-nowrap">
                                    <span className="font-medium">{index + 1}.</span>
                                    <span>{step.name || step.type}</span>
                                  </div>
                                  {index < template.workflow_steps.length - 1 && (
                                    <ArrowRight className="h-3 w-3 mx-1 text-muted-foreground" />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Initiate Dialog */}
        <Dialog open={showInitiateDialog} onOpenChange={setShowInitiateDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Play className="h-5 w-5 text-primary" />
                Initiate Workflow{selectedEmployees.size > 1 ? 's' : ''}
              </DialogTitle>
              <DialogDescription>
                Start automated outreach workflow for {selectedEmployees.size} employee{selectedEmployees.size > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-4">
              {/* Selected Employees Summary */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Selected Employees:</p>
                <div className="flex flex-wrap gap-2">
                  {Array.from(selectedEmployees).slice(0, 5).map(id => {
                    const employee = atRiskEmployees.find(e => e.employee_id === id);
                    return employee ? (
                      <Badge key={id} variant="secondary">
                        {employee.employee_name}
                      </Badge>
                    ) : null;
                  })}
                  {selectedEmployees.size > 5 && (
                    <Badge variant="outline">+{selectedEmployees.size - 5} more</Badge>
                  )}
                </div>
              </div>

              {/* Template Info */}
              {selectedTemplate && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-1">
                    Workflow Template: {templates.find(t => t.id === selectedTemplate)?.template_name}
                  </p>
                  <p className="text-xs text-blue-600">
                    {templates.find(t => t.id === selectedTemplate)?.description}
                  </p>
                </div>
              )}

              {/* Workflow Steps Preview */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Workflow will:</p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3" />
                    Send outreach via ML-selected channel (SMS, email, or phone)
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3" />
                    Wait up to 48 hours for employee response
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3" />
                    Create referral to CBO if employee confirms interest
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3" />
                    Auto-match to 4 closest providers based on location
                  </li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInitiateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleInitiateWorkflows} disabled={initiating}>
                {initiating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Initiating...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start Workflow{selectedEmployees.size > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Configure Templates Dialog */}
        <Dialog open={showConfigureDialog} onOpenChange={setShowConfigureDialog}>
          <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                Workflow Configuration
              </DialogTitle>
              <DialogDescription>
                Configure workflow templates and automation settings
              </DialogDescription>
            </DialogHeader>

            <div className="py-3 space-y-4">
              {/* Workflow Templates Summary */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Active Workflow Templates</h4>
                {templates.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground border rounded-lg">
                    <FileText className="h-6 w-6 mx-auto mb-1 opacity-50" />
                    <p className="text-xs">No workflow templates configured</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {templates.slice(0, 2).map(template => (
                      <div
                        key={template.id}
                        className="flex items-center justify-between p-2 border rounded-lg bg-muted/30"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-xs truncate">{template.template_name}</span>
                            <Badge variant={template.auto_trigger_enabled ? 'default' : 'outline'} className="text-[10px] px-1.5 py-0">
                              {template.auto_trigger_enabled ? 'Auto' : 'Manual'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                    {templates.length > 2 && (
                      <p className="text-[10px] text-muted-foreground text-center">
                        +{templates.length - 2} more
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Navigation Links */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Manage</h4>
                <div className="grid grid-cols-1 gap-2">
                  {/* Communication Templates */}
                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-2.5 px-3"
                    onClick={() => {
                      setShowConfigureDialog(false);
                      if (onNavigate) onNavigate('communication-templates');
                    }}
                  >
                    <MessageSquare className="h-4 w-4 mr-2 text-blue-600 shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="font-medium text-xs">Communication Templates</p>
                      <p className="text-[10px] text-muted-foreground truncate">SMS, email, and voice templates</p>
                    </div>
                  </Button>

                  {/* Translation Manager */}
                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-2.5 px-3"
                    onClick={() => {
                      setShowConfigureDialog(false);
                      if (onNavigate) onNavigate('integrations');
                    }}
                  >
                    <Globe className="h-4 w-4 mr-2 text-green-600 shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="font-medium text-xs">Translation Manager</p>
                      <p className="text-[10px] text-muted-foreground truncate">Multi-language message support</p>
                    </div>
                  </Button>

                  {/* Media Library / Short Links */}
                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-2.5 px-3"
                    onClick={() => {
                      setShowConfigureDialog(false);
                      if (onNavigate) onNavigate('media-library');
                    }}
                  >
                    <Link2 className="h-4 w-4 mr-2 text-purple-600 shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="font-medium text-xs">Media Library & Short Links</p>
                      <p className="text-[10px] text-muted-foreground truncate">Trackable links and attachments</p>
                    </div>
                  </Button>
                </div>
              </div>

              {/* Quick Status */}
              <div className="border-t pt-3 space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5" />
                  Status
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 p-2 border rounded-lg">
                    <Globe className="h-3.5 w-3.5 text-blue-600" />
                    <div>
                      <p className="text-[10px] text-muted-foreground">Languages</p>
                      <p className="text-xs font-medium">EN, ES, ZH, VI</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 border rounded-lg">
                    <Brain className="h-3.5 w-3.5 text-green-600" />
                    <div>
                      <p className="text-[10px] text-muted-foreground">ML Routing</p>
                      <p className="text-xs font-medium">Enabled</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="sm:justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowConfigureDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template Create/Edit Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="w-[95vw] max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingTemplate ? (
                  <>
                    <Edit2 className="h-5 w-5 text-primary" />
                    Edit Workflow Template
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5 text-primary" />
                    Create Workflow Template
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {editingTemplate
                  ? 'Modify the workflow template settings'
                  : 'Create a new automated workflow template for employee outreach'}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm border-b pb-2">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="template-name">Template Name *</Label>
                    <Input
                      id="template-name"
                      value={templateFormData.template_name || ''}
                      onChange={(e) =>
                        setTemplateFormData({ ...templateFormData, template_name: e.target.value })
                      }
                      placeholder="e.g., Critical Risk Outreach"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="template-type">Template Type</Label>
                    <Select
                      value={templateFormData.template_type || 'employee_outreach'}
                      onValueChange={(v) =>
                        setTemplateFormData({ ...templateFormData, template_type: v })
                      }
                    >
                      <SelectTrigger id="template-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee_outreach">Employee Outreach</SelectItem>
                        <SelectItem value="sdoh_support">SDOH Support</SelectItem>
                        <SelectItem value="benefits_enrollment">Benefits Enrollment</SelectItem>
                        <SelectItem value="retention_campaign">Retention Campaign</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={templateFormData.description || ''}
                    onChange={(e) =>
                      setTemplateFormData({ ...templateFormData, description: e.target.value })
                    }
                    placeholder="Describe what this workflow does..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Trigger Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm border-b pb-2">Trigger Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Target Risk Levels</Label>
                    <div className="flex flex-wrap gap-2">
                      {['critical', 'high', 'elevated', 'moderate'].map((level) => (
                        <Badge
                          key={level}
                          variant={
                            templateFormData.trigger_risk_levels?.includes(level)
                              ? 'default'
                              : 'outline'
                          }
                          className="cursor-pointer"
                          onClick={() => {
                            const current = templateFormData.trigger_risk_levels || [];
                            const updated = current.includes(level)
                              ? current.filter((l) => l !== level)
                              : [...current, level];
                            setTemplateFormData({
                              ...templateFormData,
                              trigger_risk_levels: updated,
                            });
                          }}
                        >
                          {level}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min-churn">Min Churn Probability</Label>
                    <Input
                      id="min-churn"
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={templateFormData.trigger_min_churn_probability || 0.4}
                      onChange={(e) =>
                        setTemplateFormData({
                          ...templateFormData,
                          trigger_min_churn_probability: parseFloat(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="auto-trigger"
                    checked={templateFormData.auto_trigger_enabled || false}
                    onCheckedChange={(checked) =>
                      setTemplateFormData({ ...templateFormData, auto_trigger_enabled: checked })
                    }
                  />
                  <Label htmlFor="auto-trigger" className="cursor-pointer">
                    Enable auto-trigger for matching employees
                  </Label>
                </div>
              </div>

              {/* Communication Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm border-b pb-2">Communication Settings</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="default-channel">Default Channel</Label>
                    <Select
                      value={templateFormData.default_channel || 'ml_selected'}
                      onValueChange={(v) =>
                        setTemplateFormData({ ...templateFormData, default_channel: v })
                      }
                    >
                      <SelectTrigger id="default-channel">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ml_selected">ML Selected</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timeout">Response Timeout (hours)</Label>
                    <Input
                      id="timeout"
                      type="number"
                      min="1"
                      max="168"
                      value={templateFormData.response_timeout_hours || 48}
                      onChange={(e) =>
                        setTemplateFormData({
                          ...templateFormData,
                          response_timeout_hours: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max-retries">Max Retries</Label>
                    <Input
                      id="max-retries"
                      type="number"
                      min="0"
                      max="10"
                      value={templateFormData.max_retry_attempts || 3}
                      onChange={(e) =>
                        setTemplateFormData({
                          ...templateFormData,
                          max_retry_attempts: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="ml-timing"
                    checked={templateFormData.use_ml_timing !== false}
                    onCheckedChange={(checked) =>
                      setTemplateFormData({ ...templateFormData, use_ml_timing: checked })
                    }
                  />
                  <Label htmlFor="ml-timing" className="cursor-pointer">
                    Use ML for optimal contact timing
                  </Label>
                </div>
              </div>

              {/* Referral Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm border-b pb-2">Referral Settings</h4>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="auto-referral"
                    checked={templateFormData.auto_create_referral !== false}
                    onCheckedChange={(checked) =>
                      setTemplateFormData({ ...templateFormData, auto_create_referral: checked })
                    }
                  />
                  <Label htmlFor="auto-referral" className="cursor-pointer">
                    Auto-create referral on positive response
                  </Label>
                </div>
                {templateFormData.auto_create_referral !== false && (
                  <div className="grid grid-cols-2 gap-4 pl-6">
                    <div className="space-y-2">
                      <Label htmlFor="max-providers">Max Providers to Match</Label>
                      <Input
                        id="max-providers"
                        type="number"
                        min="1"
                        max="10"
                        value={templateFormData.max_providers_to_match || 4}
                        onChange={(e) =>
                          setTemplateFormData({
                            ...templateFormData,
                            max_providers_to_match: parseInt(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2 flex items-end">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="cbo-only"
                          checked={templateFormData.cbo_only_for_sdoh !== false}
                          onCheckedChange={(checked) =>
                            setTemplateFormData({ ...templateFormData, cbo_only_for_sdoh: checked })
                          }
                        />
                        <Label htmlFor="cbo-only" className="cursor-pointer text-sm">
                          CBO only for SDOH referrals
                        </Label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing Agent Configuration */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm border-b pb-2 flex items-center gap-2">
                  🤖 AI Pricing Agent
                </h4>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="pricing-agent-enabled"
                    checked={templateFormData.pricing_agent_config?.enabled !== false}
                    onCheckedChange={(checked) =>
                      setTemplateFormData({
                        ...templateFormData,
                        pricing_agent_config: {
                          ...templateFormData.pricing_agent_config,
                          enabled: checked,
                          channel: templateFormData.pricing_agent_config?.channel || 'email',
                          strategy: templateFormData.pricing_agent_config?.strategy || 'balanced',
                          max_rounds: templateFormData.pricing_agent_config?.max_rounds || 5,
                          auto_initiate: templateFormData.pricing_agent_config?.auto_initiate !== false,
                          escalate_on_timeout: templateFormData.pricing_agent_config?.escalate_on_timeout !== false,
                          timeout_hours: templateFormData.pricing_agent_config?.timeout_hours || 48,
                        }
                      })
                    }
                  />
                  <Label htmlFor="pricing-agent-enabled" className="cursor-pointer">
                    Enable AI pricing negotiation
                  </Label>
                </div>
                {templateFormData.pricing_agent_config?.enabled !== false && (
                  <div className="space-y-3 pl-4 border-l-2 border-violet-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pricing-channel" className="text-sm">Channel</Label>
                        <Select
                          value={templateFormData.pricing_agent_config?.channel || 'email'}
                          onValueChange={(value) =>
                            setTemplateFormData({
                              ...templateFormData,
                              pricing_agent_config: {
                                ...templateFormData.pricing_agent_config,
                                enabled: templateFormData.pricing_agent_config?.enabled !== false,
                                channel: value as any,
                                strategy: templateFormData.pricing_agent_config?.strategy || 'balanced',
                                max_rounds: templateFormData.pricing_agent_config?.max_rounds || 5,
                                auto_initiate: templateFormData.pricing_agent_config?.auto_initiate !== false,
                                escalate_on_timeout: templateFormData.pricing_agent_config?.escalate_on_timeout !== false,
                                timeout_hours: templateFormData.pricing_agent_config?.timeout_hours || 48,
                              }
                            })
                          }
                        >
                          <SelectTrigger id="pricing-channel">
                            <SelectValue placeholder="Select channel" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="voice">Voice AI</SelectItem>
                            <SelectItem value="portal">Provider Portal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pricing-strategy" className="text-sm">Strategy</Label>
                        <Select
                          value={templateFormData.pricing_agent_config?.strategy || 'balanced'}
                          onValueChange={(value) =>
                            setTemplateFormData({
                              ...templateFormData,
                              pricing_agent_config: {
                                ...templateFormData.pricing_agent_config,
                                enabled: templateFormData.pricing_agent_config?.enabled !== false,
                                channel: templateFormData.pricing_agent_config?.channel || 'email',
                                strategy: value as any,
                                max_rounds: templateFormData.pricing_agent_config?.max_rounds || 5,
                                auto_initiate: templateFormData.pricing_agent_config?.auto_initiate !== false,
                                escalate_on_timeout: templateFormData.pricing_agent_config?.escalate_on_timeout !== false,
                                timeout_hours: templateFormData.pricing_agent_config?.timeout_hours || 48,
                              }
                            })
                          }
                        >
                          <SelectTrigger id="pricing-strategy">
                            <SelectValue placeholder="Select strategy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="balanced">Balanced</SelectItem>
                            <SelectItem value="aggressive">Aggressive</SelectItem>
                            <SelectItem value="accommodating">Accommodating</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="max-rounds" className="text-sm">Max Rounds</Label>
                        <Select
                          value={String(templateFormData.pricing_agent_config?.max_rounds || 5)}
                          onValueChange={(value) =>
                            setTemplateFormData({
                              ...templateFormData,
                              pricing_agent_config: {
                                ...templateFormData.pricing_agent_config,
                                enabled: templateFormData.pricing_agent_config?.enabled !== false,
                                channel: templateFormData.pricing_agent_config?.channel || 'email',
                                strategy: templateFormData.pricing_agent_config?.strategy || 'balanced',
                                max_rounds: parseInt(value),
                                auto_initiate: templateFormData.pricing_agent_config?.auto_initiate !== false,
                                escalate_on_timeout: templateFormData.pricing_agent_config?.escalate_on_timeout !== false,
                                timeout_hours: templateFormData.pricing_agent_config?.timeout_hours || 48,
                              }
                            })
                          }
                        >
                          <SelectTrigger id="max-rounds">
                            <SelectValue placeholder="Select rounds" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3">3 rounds</SelectItem>
                            <SelectItem value="5">5 rounds</SelectItem>
                            <SelectItem value="7">7 rounds</SelectItem>
                            <SelectItem value="10">10 rounds</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="timeout-hours" className="text-sm">Timeout (hours)</Label>
                        <Input
                          id="timeout-hours"
                          type="number"
                          min={1}
                          max={168}
                          value={templateFormData.pricing_agent_config?.timeout_hours || 48}
                          onChange={(e) =>
                            setTemplateFormData({
                              ...templateFormData,
                              pricing_agent_config: {
                                ...templateFormData.pricing_agent_config,
                                enabled: templateFormData.pricing_agent_config?.enabled !== false,
                                channel: templateFormData.pricing_agent_config?.channel || 'email',
                                strategy: templateFormData.pricing_agent_config?.strategy || 'balanced',
                                max_rounds: templateFormData.pricing_agent_config?.max_rounds || 5,
                                auto_initiate: templateFormData.pricing_agent_config?.auto_initiate !== false,
                                escalate_on_timeout: templateFormData.pricing_agent_config?.escalate_on_timeout !== false,
                                timeout_hours: parseInt(e.target.value) || 48,
                              }
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="auto-initiate"
                        checked={templateFormData.pricing_agent_config?.auto_initiate !== false}
                        onCheckedChange={(checked) =>
                          setTemplateFormData({
                            ...templateFormData,
                            pricing_agent_config: {
                              ...templateFormData.pricing_agent_config,
                              enabled: templateFormData.pricing_agent_config?.enabled !== false,
                              channel: templateFormData.pricing_agent_config?.channel || 'email',
                              strategy: templateFormData.pricing_agent_config?.strategy || 'balanced',
                              max_rounds: templateFormData.pricing_agent_config?.max_rounds || 5,
                              auto_initiate: checked,
                              escalate_on_timeout: templateFormData.pricing_agent_config?.escalate_on_timeout !== false,
                              timeout_hours: templateFormData.pricing_agent_config?.timeout_hours || 48,
                            }
                          })
                        }
                      />
                      <Label htmlFor="auto-initiate" className="cursor-pointer text-sm">
                        Auto-initiate when provider is selected
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="escalate-timeout"
                        checked={templateFormData.pricing_agent_config?.escalate_on_timeout !== false}
                        onCheckedChange={(checked) =>
                          setTemplateFormData({
                            ...templateFormData,
                            pricing_agent_config: {
                              ...templateFormData.pricing_agent_config,
                              enabled: templateFormData.pricing_agent_config?.enabled !== false,
                              channel: templateFormData.pricing_agent_config?.channel || 'email',
                              strategy: templateFormData.pricing_agent_config?.strategy || 'balanced',
                              max_rounds: templateFormData.pricing_agent_config?.max_rounds || 5,
                              auto_initiate: templateFormData.pricing_agent_config?.auto_initiate !== false,
                              escalate_on_timeout: checked,
                              timeout_hours: templateFormData.pricing_agent_config?.timeout_hours || 48,
                            }
                          })
                        }
                      />
                      <Label htmlFor="escalate-timeout" className="cursor-pointer text-sm">
                        Escalate to human if no agreement reached
                      </Label>
                    </div>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm border-b pb-2">Status</h4>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is-active"
                    checked={templateFormData.is_active !== false}
                    onCheckedChange={(checked) =>
                      setTemplateFormData({ ...templateFormData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is-active" className="cursor-pointer">
                    Template is active
                  </Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowTemplateDialog(false);
                  setEditingTemplate(null);
                  setTemplateFormData({});
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
                {savingTemplate ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Employee Detail Panel - Shows consent, referral, workflow status with staff actions */}
        <EmployeeDetailPanel
          employeeId={selectedEmployeeForDetail?.id || null}
          employeeName={selectedEmployeeForDetail?.name}
          isOpen={!!selectedEmployeeForDetail}
          onClose={() => setSelectedEmployeeForDetail(null)}
          onRefresh={fetchDashboardData}
        />
      </div>
    </TooltipProvider>
  );
};

export default EmployerWorkflowOrchestration;
